import { isArray } from '../packages/array.js';
import { installEverydayHealthCore, type EverydayHealthCore } from '../../source/shared/pilot-boundaries.js';
import * as core from '@hephesapla/core-validation';
const endpoints: EverydayHealthCore = {
lpg: { validateConfig(config) {
		const metadata = core.resolveMetadata(config?.metadata, core.DEFAULT_LPG_PAYBACK_CONFIG.metadata);
		if (!config || typeof config !== "object") return { errors: [{
			field: "config",
			code: "INVALID_CONFIG",
			message: "LPG amortisman konfigürasyonu geçersiz."
		}], metadata: metadata };
		const configErrors = core.simpleConfigErrors(config, "lpg-payback", core.LPG_PAYBACK_NOTES);
		if (configErrors.length > 0) return { errors: configErrors, metadata: metadata };
		
return { errors: [], metadata };
}, calculate(input, metadata) {const extraOneOffCost = input.extraOneOffCost;
		const gasolineCostPerKm = input.gasolinePricePerLitre * input.gasolineConsumptionL100Km / 100;
		const lpgCostPerKm = input.lpgPricePerLitre * input.lpgConsumptionL100Km / 100;
		const savingsPerKm = gasolineCostPerKm - lpgCostPerKm;
		const investment = input.conversionCost + extraOneOffCost;
		if (gasolineCostPerKm <= 0 || lpgCostPerKm <= 0 || investment <= 0) return core.failure([{
			field: "result",
			code: "CALCULATION_OVERFLOW",
			message: "Girdi ölçeği güvenilir bir LPG amortisman sonucu üretmek için çok küçüktür."
		}], metadata);
		const monthlySavings = savingsPerKm * input.monthlyDistanceKm;
		const annualSavings = monthlySavings * 12;
		const paybackPossible = savingsPerKm > 0;
		const paybackDistanceKm = paybackPossible ? investment / savingsPerKm : null;
		const paybackMonths = paybackDistanceKm === null ? null : paybackDistanceKm / input.monthlyDistanceKm;
		if (paybackPossible && (savingsPerKm * 100 <= 0 || monthlySavings <= 0 || annualSavings <= 0)) return core.failure([{
			field: "result",
			code: "CALCULATION_OVERFLOW",
			message: "Girdi ölçeği güvenilir bir LPG tasarruf sonucu üretmek için çok küçüktür."
		}], metadata);
		const numbers = [
			gasolineCostPerKm,
			lpgCostPerKm,
			savingsPerKm,
			investment,
			monthlySavings,
			annualSavings,
			...paybackDistanceKm === null || paybackMonths === null ? [] : [paybackDistanceKm, paybackMonths]
		];
		return core.finiteOutput({
			...input,
			extraOneOffCost,
			gasolineCostPerKm,
			lpgCostPerKm,
			savingsPerKm,
			savingsPer100Km: savingsPerKm * 100,
			investment,
			monthlySavings,
			annualSavings,
			paybackPossible,
			paybackDistanceKm,
			paybackMonths
		}, [...numbers, savingsPerKm * 100], metadata);
	} },
fuel: { validateConfig(config) {
		const metadata = core.resolveMetadata(config?.metadata, core.DEFAULT_FUEL_CONSUMPTION_CONFIG.metadata);
		if (!config || typeof config !== "object") return { errors: [{
			field: "config",
			code: "INVALID_CONFIG",
			message: "Yakıt tüketimi konfigürasyonu geçersiz."
		}], metadata: metadata };
		const configErrors = [...core.validateMetadata(config.metadata)];
		configErrors.push(...core.productMetadataErrors(config.metadata, "fuel-consumption", core.FUEL_CONSUMPTION_NOTES));
		for (const field of [
			"distanceKm",
			"consumptionL100Km",
			"pricePerLitre"
		] as const) {
			const error = core.validateRangeConfig(`config.${field}`, config[field], core.MIN_STABLE_POSITIVE_INPUT);
			if (error) configErrors.push(error);
		}
		if (configErrors.length > 0) return { errors: configErrors, metadata: metadata };
		
return { errors: [], metadata };
}, calculate(input, metadata) {
		const effectiveDistanceKm = input.direction === "gidisDonus" ? input.distanceKm * 2 : input.distanceKm;
		const consumedLitres = effectiveDistanceKm * input.consumptionL100Km / 100;
		const costPer100Km = input.consumptionL100Km * input.pricePerLitre;
		const costPerKm = costPer100Km / 100;
		const totalCost = consumedLitres * input.pricePerLitre;
		if (effectiveDistanceKm <= 0 || consumedLitres <= 0 || costPerKm <= 0 || costPer100Km <= 0 || totalCost <= 0) return core.failure([{
			field: "result",
			code: "CALCULATION_OVERFLOW",
			message: "Girdi ölçeği güvenilir bir yakıt tüketimi sonucu üretmek için çok küçüktür."
		}], metadata);
		return core.finiteOutput({
			...input,
			effectiveDistanceKm,
			consumedLitres,
			costPerKm,
			costPer100Km,
			totalCost
		}, [
			effectiveDistanceKm,
			consumedLitres,
			costPerKm,
			costPer100Km,
			totalCost
		], metadata);
	} },
profit: { validateConfig(config) {
		const metadata = core.resolveMetadata(config?.metadata, core.DEFAULT_PROFIT_MARGIN_CONFIG.metadata);
		if (!config || typeof config !== "object") return { errors: [{
			field: "config",
			code: "INVALID_CONFIG",
			message: "Kâr marjı konfigürasyonu geçersiz."
		}], metadata: metadata };
		const configErrors = [...core.validateMetadata(config.metadata), ...core.productMetadataErrors(config.metadata, "profit-margin", core.PROFIT_MARGIN_NOTES)];
		for (const [field, bounds, minimumAllowed] of [
			[
				"monetaryInput",
				config.monetaryInput,
				core.MIN_STABLE_MONETARY_INPUT
			],
			[
				"targetMarginPercent",
				config.targetMarginPercent,
				0
			],
			[
				"markupPercent",
				config.markupPercent,
				0
			]
		] as const) {
			const error = core.validateRangeConfig(`config.${field}`, bounds, minimumAllowed);
			if (error) configErrors.push(error);
		}
		if (isArray(config.targetMarginPercent) && config.targetMarginPercent.length === 2 && core.isFiniteNumber(config.targetMarginPercent[1]) && config.targetMarginPercent[1] >= 100) configErrors.push({
			field: "config.targetMarginPercent",
			code: "INVALID_CONFIG",
			message: "Hedef satış marjı üst sınırı yüzde 100 değerinden küçük olmalıdır."
		});
		if (configErrors.length > 0) return { errors: configErrors, metadata: metadata };
		
return { errors: [], metadata };
}, calculate(input, metadata) {const record = input; const mode = input.mode;
		const validatedMode = mode;
		const cost = record.cost;
		const salePrice = record.mode === "targetSaleMargin" ? cost / (1 - record.targetMarginPercent / 100) : record.mode === "costMarkup" ? cost * (1 + record.markupPercent / 100) : record.salePrice;
		const profit = salePrice - cost;
		if ((record.mode === "targetSaleMargin" ? record.targetMarginPercent : record.mode === "costMarkup" ? record.markupPercent : 0) > 0 && profit === 0) return core.failure([{
			field: "result",
			code: "CALCULATION_OVERFLOW",
			message: "Girdi ölçeği güvenilir bir kâr marjı sonucu üretmek için çok küçüktür."
		}], metadata);
		const markupPercent = profit / cost * 100;
		const marginPercent = profit / salePrice * 100;
		return core.finiteOutput({
			mode: validatedMode,
			cost,
			salePrice,
			profit,
			markupPercent,
			marginPercent,
			isLoss: profit < 0
		}, [
			salePrice,
			profit,
			markupPercent,
			marginPercent
		], metadata);
	} },
bmr: { validateConfig(config) {
		const metadata = core.resolveMetadata(config?.metadata, core.DEFAULT_BMR_CONFIG.metadata);
		if (!config || typeof config !== "object") return { errors: [{
			field: "config",
			code: "INVALID_CONFIG",
			message: "BMR konfigürasyonu geçersiz."
		}], metadata: metadata };
		const configErrors = core.bmrConfigErrors(config);
		if (configErrors.length > 0) return { errors: configErrors, metadata: metadata };
		
return { errors: [], metadata };
}, calculate(input, metadata) {
		const sexConstant = input.sex === "erkek" ? 5 : -161;
		const mifflinKcalPerDay = Math.max(0, 10 * input.weightKg + 6.25 * input.heightCm - 5 * input.ageYears + sexConstant);
		const revisedHarrisBenedictKcalPerDay = Math.max(0, input.sex === "erkek" ? 88.362 + 13.397 * input.weightKg + 4.799 * input.heightCm - 5.677 * input.ageYears : 447.593 + 9.247 * input.weightKg + 3.098 * input.heightCm - 4.33 * input.ageYears);
		const selectedKcalPerDay = input.formula === "mifflin" ? mifflinKcalPerDay : revisedHarrisBenedictKcalPerDay;
		const selectedKcalPerHour = selectedKcalPerDay / 24;
		const selectedSourceScope = input.formula === "mifflin" ? input.ageYears >= 19 && input.ageYears <= 78 ? "within-cited-age-range" : "outside-cited-age-range" : "cited-age-range-not-explicit";
		const selectedSourceScopeNote = input.formula === "mifflin" ? selectedSourceScope === "within-cited-age-range" ? "Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığındadır." : "Seçilen yaş, Mifflin–St Jeor çalışmasında bildirilen 19–78 yaş aralığının dışındadır; sonuç ekstrapolasyondur." : "Roza–Shizgal PubMed özetinde kesin alt ve üst yaş sınırı verilmediği için yaş kapsamı ayrıca doğrulanamamıştır.";
		return core.finiteHealthResult({
			...input,
			mifflinKcalPerDay,
			revisedHarrisBenedictKcalPerDay,
			selectedKcalPerDay,
			selectedKcalPerHour,
			selectedSourceScope,
			selectedSourceScopeNote
		}, [
			mifflinKcalPerDay,
			revisedHarrisBenedictKcalPerDay,
			selectedKcalPerDay,
			selectedKcalPerHour
		], metadata);
	} },
idealWeight: { validateConfig(config) {
		const metadata = core.resolveMetadata(config?.metadata, core.DEFAULT_IDEAL_WEIGHT_CONFIG.metadata);
		if (!config || typeof config !== "object") return { errors: [{
			field: "config",
			code: "INVALID_CONFIG",
			message: "İdeal kilo referans konfigürasyonu geçersiz."
		}], metadata: metadata };
		const configErrors = core.idealWeightConfigErrors(config);
		if (configErrors.length > 0) return { errors: configErrors, metadata: metadata };
		
return { errors: [], metadata };
}, calculate(input, metadata) {
		const inchesOverFiveFeet = input.heightCm / 2.54 - 60;
		const devineKg = Math.max(0, (input.sex === "erkek" ? 50 : 45.5) + 2.3 * inchesOverFiveFeet);
		const robinsonKg = Math.max(0, (input.sex === "erkek" ? 52 : 49) + (input.sex === "erkek" ? 1.9 : 1.7) * inchesOverFiveFeet);
		const hamwiKg = Math.max(0, (input.sex === "erkek" ? 48 : 45.5) + (input.sex === "erkek" ? 2.7 : 2.2) * inchesOverFiveFeet);
		const referenceMinimumKg = Math.min(devineKg, robinsonKg, hamwiKg);
		const referenceMaximumKg = Math.max(devineKg, robinsonKg, hamwiKg);
		const currentWeightKg = input.currentWeightKg ?? null;
		const heightSourceScope = input.heightCm >= 152.4 ? "within-five-foot-reference-basis" : "below-five-foot-extrapolation";
		const heightSourceScopeNote = heightSourceScope === "within-five-foot-reference-basis" ? "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasındadır veya üzerindedir." : "Boy, tarihsel formüllerin 5 fit (152,4 cm) başlangıç noktasının altındadır; sonuç geriye doğru ekstrapolasyondur.";
		let currentPosition = null;
		let differenceFromRangeKg = null;
		if (currentWeightKg !== null) {
			if (currentWeightKg < referenceMinimumKg) {
				currentPosition = "below";
				differenceFromRangeKg = referenceMinimumKg - currentWeightKg;
			} else if (currentWeightKg > referenceMaximumKg) {
				currentPosition = "above";
				differenceFromRangeKg = currentWeightKg - referenceMaximumKg;
			} else {
				currentPosition = "within";
				differenceFromRangeKg = 0;
			}
		}
		return core.finiteHealthResult({
			sex: input.sex,
			heightCm: input.heightCm,
			currentWeightKg,
			devineKg,
			robinsonKg,
			hamwiKg,
			referenceMinimumKg,
			referenceMaximumKg,
			currentPosition,
			differenceFromRangeKg,
			heightSourceScope,
			heightSourceScopeNote
		}, [
			devineKg,
			robinsonKg,
			hamwiKg,
			referenceMinimumKg,
			referenceMaximumKg,
			...differenceFromRangeKg === null ? [] : [differenceFromRangeKg]
		], metadata);
	} },
};
installEverydayHealthCore(endpoints);
